import { queryOptions, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { requireWorkspaceId } from '../shared/workspace';
import type { NewsletterContentPreview, NewsletterContentPreviewRequest } from './entity';

/** A Design Studio SMS body (`<x-sms>`) rendered by the server (`previews/message_channel`). */
export async function renderDsSmsBody(workspaceId: number, body: string, signal?: AbortSignal): Promise<string> {
  const response = await hydraUiApi.previews.messageChannel({
    params: { path: { environment_id: String(workspaceId) } },
    body: { template_type: 'sms', template_body: body },
    signal,
  });
  return response.template_body_display;
}

// Dedupes close-together mounts of the same body within a view (Ember caches a render for 20s).
const SMS_RENDER_STALE_TIME_MS = 20_000;

const dsSmsBodyQueryOptions = (workspaceId: number, body: string) =>
  queryOptions({
    queryKey: ['preview', workspaceId, 'ds-sms-body', body] as const,
    queryFn: ({ signal }) => renderDsSmsBody(workspaceId, body, signal),
    staleTime: SMS_RENDER_STALE_TIME_MS,
    retry: false,
  });

/** The server-rendered text of a Design Studio SMS body; idle when `enabled` is false. */
export function useDsSmsBody(body: string, enabled: boolean) {
  return useQuery({ ...dsSmsBodyQueryOptions(requireWorkspaceId(), body), enabled });
}

export async function getNewsletterContentPreview(
  workspaceId: number,
  request: NewsletterContentPreviewRequest,
  signal?: AbortSignal,
): Promise<NewsletterContentPreview> {
  const params = { path: { environment_id: String(workspaceId) } };
  switch (request.type) {
    case 'push':
      return {
        type: request.type,
        data: await hydraUiApi.previews.push({ params, body: request.body, signal }),
      };
    case 'in_app':
      return {
        type: request.type,
        data: await hydraUiApi.previews.inApp({ params, body: request.body, signal }),
      };
    case 'email':
      return {
        type: request.type,
        data: await hydraUiApi.previews.email({ params, body: request.body, signal }),
      };
    case 'sms':
      return {
        type: request.type,
        data: await hydraUiApi.previews.messageChannel({ params, body: request.body, signal }),
      };
    case 'twilio':
      return {
        type: request.type,
        data: await hydraUiApi.previews.twilio({ params, body: request.body, signal }),
      };
    case 'webhook':
      return {
        type: request.type,
        data: await hydraUiApi.previews.webhook({ params, body: request.body, signal }),
      };
    case 'inbox':
      return {
        type: request.type,
        data: await hydraUiApi.previews.inbox({ params, body: request.body, signal }),
      };
    default: {
      const exhaustive: never = request;
      return exhaustive;
    }
  }
}
